import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';

/**
 * Generic searchable dropdown.
 *
 * Props:
 *   value         currently selected id (any primitive)
 *   onChange      (id, item) => void
 *   items         array of records
 *   getValue      (item) => id                        default: item.id
 *   getLabel      (item) => string                    default: item.name || item.label
 *   getSublabel   (item) => string | null             default: null
 *   getSearchText (item) => string (space-joined)     default: getLabel + ' ' + getSublabel
 *   placeholder   string
 *   emptyLabel    "Select…" placeholder when nothing selected
 *   testId        data-testid prefix
 *   disabled
 *   allowClear    show a "None" option at the top (id = "")
 *   noneLabel     label for the None option
 */
export default function SearchableSelect({
  value,
  onChange,
  items = [],
  getValue = (i) => i.id,
  getLabel = (i) => i.name || i.label || '',
  getSublabel = null,
  getSearchText = null,
  placeholder = 'Search…',
  emptyLabel = 'Select',
  testId = 'searchable-select',
  disabled = false,
  allowClear = false,
  noneLabel = '— None —',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => items.find(i => getValue(i) === value) || null,
    [items, value, getValue]
  );

  const buildSearchText = (item) => {
    if (getSearchText) return getSearchText(item);
    const l = getLabel(item);
    const s = getSublabel ? getSublabel(item) : '';
    return `${l} ${s}`;
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i => buildSearchText(i).toLowerCase().includes(q));
  }, [items, query]); // eslint-disable-line

  useEffect(() => { setHighlight(0); }, [filtered.length, open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const selectItem = (item) => {
    onChange(getValue(item), item);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const item = filtered[highlight];
      if (item) selectItem(item);
    }
    else if (e.key === 'Escape') { setOpen(false); }
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`} data-testid={`${testId}-root`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        className={`w-full flex items-center justify-between rounded border border-blue-200 bg-white px-3 py-2 text-sm text-left h-10 disabled:opacity-50 disabled:cursor-not-allowed ${open ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
        data-testid={`${testId}-trigger`}
      >
        <span className={`truncate ${selected ? 'text-slate-900' : 'text-slate-400'}`}>
          {selected
            ? getLabel(selected) + (getSublabel ? (getSublabel(selected) ? ` — ${getSublabel(selected)}` : '') : '')
            : emptyLabel}
        </span>
        <ChevronDown className={`h-4 w-4 text-slate-400 flex-shrink-0 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg overflow-hidden" data-testid={`${testId}-panel`}>
          <div className="flex items-center border-b border-slate-200 px-2">
            <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1 h-9 px-2 text-sm bg-transparent outline-none"
              data-testid={`${testId}-input`}
            />
            {query && (
              <button type="button" onClick={() => { setQuery(''); inputRef.current?.focus(); }}
                className="text-slate-400 hover:text-slate-600" aria-label="Clear search">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="max-h-64 overflow-y-auto" role="listbox">
            {allowClear && (
              <button type="button"
                onClick={() => { onChange('', null); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-sm text-slate-500 italic border-b border-slate-100 hover:bg-slate-50 ${value === '' || value === null || value === undefined ? 'font-semibold text-blue-700' : ''}`}
                data-testid={`${testId}-option-none`}
              >
                {noneLabel}
              </button>
            )}
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-slate-500" data-testid={`${testId}-empty`}>
                {items.length === 0 ? 'No options available' : `No match for "${query}"`}
              </div>
            ) : (
              filtered.map((item, idx) => {
                const id = getValue(item);
                const isSelected = id === value;
                const sub = getSublabel ? getSublabel(item) : null;
                return (
                  <button key={id} type="button"
                    onClick={() => selectItem(item)}
                    onMouseEnter={() => setHighlight(idx)}
                    className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 ${idx === highlight ? 'bg-blue-50' : 'hover:bg-slate-50'} ${isSelected ? 'font-semibold text-blue-700' : 'text-slate-900'}`}
                    data-testid={`${testId}-option-${id}`}
                  >
                    <div className="truncate">{getLabel(item) || '(no label)'}</div>
                    {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
                  </button>
                );
              })
            )}
          </div>
          <div className="px-3 py-1.5 border-t border-slate-100 text-[10px] text-slate-400 bg-slate-50">
            {filtered.length} of {items.length} · ↑↓ to navigate · Enter to select
          </div>
        </div>
      )}
    </div>
  );
}
